/**
 * Canonical cross-service contract between `apps/services/conversation`
 * (publisher of `AgentsEvent`, consumer of `AgentsReply`) and
 * `apps/services/agents` (consumer of `AgentsEvent`, publisher of
 * `AgentsReply`). Schemas live here so both services parse the same
 * shapes from one source of truth.
 *
 * Both top-level wire shapes carry `.strict()` — extra keys surface as
 * `unrecognized_keys` parse errors at every consumer. Nested object schemas
 * retain Zod's default strip behavior, matching `MessageEnvelopeSchema`.
 * Wire identifiers use the shared branded conversation/message schemas, and
 * `correlationId` alone links a reply to its originating event.
 */

import { z } from 'zod';

import { CardIdSchema } from '../card/card_id';
import { NotificationTypeSchema } from '../notification/types';
import { LocaleSchema } from '../traveler/travel_preferences';
import { MessageAuthorSchema } from './message_author';
import { ConversationIdSchema, MessageIdSchema } from './message_envelope';
import { EnvelopeOrgIdSchema } from './org_id';
import { SseEventSchema } from './sse_event';

export const AgentsEventMessageSchema = z.object({
  // Canonical platform vocabulary (see `MessageAuthorSchema`). At the LLM
  // boundary the agents service maps these to provider message roles in
  // `toBaseMessages()` (`customer → user`, `agent`/`operator → assistant`,
  // `system → system`).
  author: MessageAuthorSchema,
  content: z.string(),
  at: z.iso.datetime(),
});

export const AgentsEventSourceSchema = z.object({
  service: z.string().min(1),
  // Carries every chat channel the conversation envelope can produce
  // (`ChatChannelSchema`: whatsapp/email/sms/phone/slack/teams/app/zendesk/sunshine)
  // so the channel propagates losslessly to the card's `originChannel`, plus
  // `web`/`api` for non-conversational agent ingress.
  channel: z
    .enum([
      'whatsapp',
      'web',
      'api',
      'slack',
      'email',
      'sms',
      'phone',
      'teams',
      'app',
      'zendesk',
      'sunshine',
    ])
    .optional(),
  userId: z.string().optional(),
  // Tags `userId` as the opaque customer publicId ('customer') or the anonymous
  // binding _id ('anon') so the agents service can tag the Langfuse trace
  // (identity:<kind>) without inspecting the id format. Optional → backward-compatible.
  userIdKind: z.enum(['customer', 'anon']).optional(),
});

// Phase 5 D5-06: identity snapshot injected into every AgentsEvent so the
// agents service can decide whether a step-up is required. Inline `loa` enum —
// the conversation-local loa schema stays service-local (D-11), never imported here.
// ladder loa0-loa2; sessão app ≡ loa2 — channel-switch design 2026-06-12
export const IdentityContextSchema = z.object({
  loa: z.enum(['loa0', 'loa1', 'loa2']),
  linked: z.boolean(),
  vollUserId: z.string().min(1).optional(),
});

export type IdentityContext = z.infer<typeof IdentityContextSchema>;

// Phase 5 D5-05: auth directive the agents service sends back when a sensitive
// action needs a higher LoA. `pendingIntent` is opaque (downstream whitelists
// fields on reconstruction — no eval). Inline `requiredLoa` enum (D-11).
// request_login/request_step_up mudam o BINDING (identidade) e mantêm o canal
// (magic link). request_channel_switch muda a CONVERSA de canal — link simples
// do web chat, thread migra (flip in-place no conversation).
export const AuthDirectiveSchema = z.object({
  action: z.enum(['request_login', 'request_step_up', 'request_channel_switch']),
  requiredLoa: z.enum(['loa1', 'loa2']),
  pendingIntent: z.record(z.string(), z.unknown()).optional(),
  // O idioma que a copy determinística DESTE turno usou no agents (#3072). O
  // conversation reusa exatamente este valor nas mensagens que o directive
  // gera (link do channel-switch, desafio de magic link, fechamento na
  // chegada) em vez de re-resolver a cascata por conta própria — duas fontes
  // de idioma no mesmo turno é o que fazia o aviso sair em `en` e o link em
  // pt-BR. Opcional + aditivo: producer que não o emite mantém a cascata
  // anterior (`conversation.customerLanguage` → pt-BR).
  locale: LocaleSchema.optional(),
});

export type AuthDirective = z.infer<typeof AuthDirectiveSchema>;

export const AgentsEventSchema = z
  .object({
    messageId: MessageIdSchema,
    correlationId: z.uuid(),
    conversationId: ConversationIdSchema,
    // Trip association owned by conversation. Optional + additive so unscoped
    // conversations remain valid; when present, agents-v2 treats it as the
    // authoritative trip context for this thread.
    tripId: z.string().min(1).max(100).optional(),
    orgId: EnvelopeOrgIdSchema,
    source: AgentsEventSourceSchema,
    // v2 (kanban split): the conversation service creates the card and mints its
    // id, then passes it here so agents can MANAGE the card's tasks (born-blocked
    // on escalation, completed on done) directly in its supervisor graph. The
    // conversation owns card CREATION; agents owns task lifecycle. Optional +
    // additive → v1 producers omit it (and agents-v2 no-ops kanban without it).
    cardId: CardIdSchema.optional(),
    domainHint: z
      .enum([
        'hotel',
        'flight',
        'mobility',
        'rental',
        'bus',
        'events',
        'flight_schedule_change',
        'approvals',
      ])
      .nullish(),
    messages: z.array(AgentsEventMessageSchema).min(1),
    metadata: z.record(z.string(), z.unknown()).optional(),
    deadlineAt: z.iso.datetime().optional(),
    // Phase 5 D5-06: .optional() mandatory — the .strict() schema must stay
    // backward-compatible with the not-yet-updated agents consumer.
    identityContext: IdentityContextSchema.optional(),
    // A signal, not a turn: the Zendesk desk released the conversation, so the
    // human gate is over and agents completes the escalation task it holds (no
    // graph, no reply). Top-level ON PURPOSE, unlike `handback` below: an agents
    // that predates the field must DROP the event (`.strict()` rejects it), never
    // run its system message as a customer turn during a rolling deploy.
    deskReleased: z.literal(true).optional(),
  })
  .strict();

export type AgentsEvent = z.infer<typeof AgentsEventSchema>;
export type AgentsEventMessage = z.infer<typeof AgentsEventMessageSchema>;

/**
 * `AgentsEvent.metadata` key: `true` means a human just handed this
 * conversation back to the AI (a Zendesk desk release or redirect) — the
 * agents service must reset any escalated state (e.g. hotel
 * `phase: 'escalated'`), otherwise it keeps answering `already_escalated`
 * ("Um atendente já está com a sua conversa") after control has moved on.
 * Lives on the open `metadata` record instead of a new top-level field so the
 * `.strict()` `AgentsEventSchema` stays backward-compatible.
 */
export const AGENTS_EVENT_HANDBACK_KEY = 'handback';

/** True only when `metadata[AGENTS_EVENT_HANDBACK_KEY] === true`. */
export function isHandbackEvent(event: Pick<AgentsEvent, 'metadata'>): boolean {
  return event.metadata?.[AGENTS_EVENT_HANDBACK_KEY] === true;
}

/**
 * `AgentsEvent.metadata` key: the proactive notification this customer turn
 * answers (a WhatsApp reply to a flight alert). Set by conversation only when
 * it linked the reply to a notification — by the quoted message (`reply_to`)
 * or, without a quote, by the contact's latest proactive thread in the last
 * 24h (`recent_proactive`, a heuristic) — or a web-chat turn in the app
 * conversation an inbox item opened as (`notice_thread`, #6277). `title`/`flightLine` are the
 * platform-rendered alert lines (inbox projection), never customer text.
 * Lives on the open `metadata` record so the `.strict()` event stays
 * backward-compatible; consumers parse it with `RepliedNotificationSchema`.
 */
export const AGENTS_EVENT_REPLIED_NOTIFICATION_KEY = 'repliedNotification';

export const RepliedNotificationSchema = z.object({
  notificationType: NotificationTypeSchema,
  linkedBy: z.enum(['reply_to', 'recent_proactive', 'notice_thread']),
  title: z.string().min(1).max(300),
  flightLine: z.string().min(1).max(300).optional(),
  tripId: z.string().min(1).max(100).optional(),
});

export type RepliedNotification = z.infer<typeof RepliedNotificationSchema>;

export const ToolCallTraceSchema = z.object({
  agent: z.string(),
  tool: z.string(),
  durationMs: z.number(),
  error: z.string().nullable(),
});

export const AgentTraceEntrySchema = z.object({
  agent: z.string(),
  at: z.iso.datetime(),
  // Optional, low-cardinality marker for WHY a node produced its turn (e.g.
  // 'policy_blocked', 'hint', 'low_confidence_fallback'). Backward-compatible:
  // producers that don't set it omit it; consumers that don't read it ignore it.
  // Surfaces deterministic gate decisions on the wire so downstream
  // (conversation/BI) can distinguish a block from a normal answer.
  reason: z.string().optional(),
});

// Um PASSO do raciocínio do turno — o que o agente DE FATO fez, em id
// determinístico. O texto é do consumidor (o web-chat traduz o id por locale):
// nada do LLM, do Google nem do fornecedor viaja aqui.
//
// PRIVACIDADE (regra dura, decisão 13/09/2026): o id vem de um conjunto FECHADO
// por domínio e as variáveis têm chave fechada e origem única —
// `hotel` = título da oferta EM CENA, `place`/`city` = o texto que o PRÓPRIO
// cliente escreveu, `count` = um número. Nunca ids (offerId, sessionId,
// placeId), preços, valores/limites de política, nomes de viajantes, texto de
// avaliação, endereço, coordenada, nem qualquer string do modelo ou de
// terceiros.
export const TurnStepVarKeySchema = z.enum(['hotel', 'place', 'city', 'count']);
export type TurnStepVarKey = z.infer<typeof TurnStepVarKeySchema>;

/** Teto de cada variável de passo. O PRODUTOR corta aqui antes de emitir
 *  (título de hotel longo não pode derrubar a resposta no parse). */
export const TURN_STEP_VAR_MAX_CHARS = 80;

/** Os ids de passo — lista FECHADA compartilhada por quem produz (agents) e
 *  por quem traduz (web-chat): um id fora daqui é deriva de contrato e reprova
 *  no parse, em vez de virar "Trabalhei nisso" em silêncio. */
export const TurnStepIdSchema = z.enum([
  'search',
  'policy_check',
  'read_scene',
  'rank_by_place',
  'open_hotel',
  'show_rooms',
  'consult_reviews',
  'consult_surroundings',
  'consult_travel',
  'consult_sheet',
  'consult_area',
  'consult_place',
  'consult_documents',
  'tool_place_details',
  'tool_find_places',
  'tool_travel_time',
  'tool_geocode',
  'tool_check_travel_documents',
  'write_answer',
  'ask_client',
  'show_again',
  'checkout',
  'book',
]);
export type TurnStepId = z.infer<typeof TurnStepIdSchema>;

/** Os ids de resumo — a linha acima da resposta. `handled` é o fallback. */
export const TurnSummaryIdSchema = z.enum([
  'searched',
  'answered',
  'consulted',
  'opened',
  'asked',
  'checkout',
  // Desfechos da reserva (#5514): `booked` só no sucesso; o turno que não
  // fechou diz o que de fato aconteceu em vez de "Fechei a reserva".
  'booked',
  'booking_pending',
  'booking_failed',
  // A recepção respondeu em texto livre (recusa educada, esclarecimento) — a
  // linha neutra "Respondi" (#3402); `answered` fala "pelo que está na tela",
  // que a recepção não tem.
  'replied',
  'handled',
]);
export type TurnSummaryId = z.infer<typeof TurnSummaryIdSchema>;

export const TurnStepSchema = z
  .object({
    id: TurnStepIdSchema,
    // `partialRecord` e NÃO `record`: na v4 do zod um record de chave enum é
    // EXAUSTIVO (exigiria as quatro chaves em todo passo). Chave fora do enum
    // continua sendo rejeitada nas duas formas — que é a cerca que importa.
    vars: z.partialRecord(TurnStepVarKeySchema, z.string().max(TURN_STEP_VAR_MAX_CHARS)).optional(),
  })
  .strict();

export type TurnStep = z.infer<typeof TurnStepSchema>;

/** Teto de passos por turno — o bloco recolhido é um resumo, não um log. */
export const MAX_TURN_STEPS = 8;

export const AgentsReplyTraceSchema = z.object({
  domain: z.string().nullable(),
  finalAgent: z.string().nullable(),
  agentTrace: z.array(AgentTraceEntrySchema),
  toolCalls: z.array(ToolCallTraceSchema),
  durationMs: z.number(),
  // Id OTel do span `agents.turn` (== id do trace no Langfuse v5). Opcional e
  // aditivo: o conversation o persiste no doc outbound para o feedback pontuar
  // o turno certo. Ausente em producers antigos e no error reply.
  traceId: z
    .string()
    .regex(/^[0-9a-f]{32}$/)
    .optional(),
  // Os passos que o turno DE FATO deu (ver `TurnStepSchema`). Opcional e
  // aditivo: producer que não os emite (domínio sem passos, error reply) o
  // omite, e o consumidor cai no roteiro narrado de sempre.
  steps: z.array(TurnStepSchema).max(MAX_TURN_STEPS).optional(),
  // O id do RESUMO do turno — a linha que fica acima da resposta.
  summary: TurnSummaryIdSchema.optional(),
});

// Handoff brief produced by the agents service on a `handoff_to_human` reply.
// CHANNEL-AGNOSTIC by design: it carries the SEMANTIC summary the receiving
// human needs, never any channel-specific ticket encoding. Each channel adapter
// interprets it for its surface — sunshine maps `summary` into the Sunco
// passControl `metadata` (`dataCapture.systemField.comment.body`) so it becomes
// the opening comment of the Zendesk ticket. Optional + additive (like
// `authDirective`) so the `.strict()` schema stays backward-compatible with
// producers that don't emit it.
export const HandoffMetaSchema = z.object({
  // The conversation brief for the human agent (cap mirrors the kanban card
  // `summary`). Present only when the agents service generated one.
  summary: z.string().max(2000).optional(),
});

export type HandoffMeta = z.infer<typeof HandoffMetaSchema>;

// v2 lifecycle signal — the agent's SEMANTIC outcome of the turn. agents-v2
// emits this and knows NOTHING about kanban; the conversation service maps it
// to board actions. The reply `status` is DERIVED from it (single source) via
// `deriveReplyStatus`. `blocked` (v1 parity: `block_current_task`) is a
// task pausing for human review/awareness WITHOUT handing the conversation
// off — the AI stays in command with the customer, `status` stays `'ok'`, and
// only the Kanban task pauses. Distinct from `escalated` (`escalate_to_human`),
// which hands the conversation itself to a human (`status: 'handoff_to_human'`).
// Neither is the same as "awaiting input"/"awaiting client", which stay
// internal FSM/conversation concerns and are never an agent-reply signal.
// Optional + additive (like `authDirective`/`handoff`) so the `.strict()`
// schema stays backward-compatible with v1 producers that don't emit it.
export const AgentsLifecycleSchema = z.enum(['escalated', 'blocked', 'completed', 'none']);

export type AgentsLifecycle = z.infer<typeof AgentsLifecycleSchema>;

export const AgentsReplySchema = z
  .object({
    messageId: MessageIdSchema,
    correlationId: z.uuid(),
    conversationId: ConversationIdSchema,
    orgId: EnvelopeOrgIdSchema,
    status: z.enum(['ok', 'handoff_to_human', 'error']),
    response: z
      .object({
        text: z.string(),
        payload: z.record(z.string(), z.unknown()).optional(),
        // Phase 2 (proactive-redirect PNR-04/PNR-05): optional canonical
        // notification turn. When present, the conversation `agents_reply_handler`
        // builds an `OutboundNotification` (template UTILITY + redirect) instead
        // of persisting `text` — `text` is '' on a notification turn. Kept loose
        // as `z.string()` here (the wire stays string-typed); the canonical
        // narrowing to `NotificationType` happens downstream when conversation
        // assembles the `OutboundNotification`. Optional + nested-strip (the
        // `.strict()` is top-level only, see header) → producers that don't emit
        // it (every existing agent) never trip `unrecognized_keys`.
        notification: z
          .object({
            notificationType: z.string(),
            vars: z.record(z.string(), z.string()),
          })
          .optional(),
      })
      .nullable(),
    trace: AgentsReplyTraceSchema,
    error: z.object({ code: z.string(), message: z.string() }).nullable(),
    publishedAt: z.iso.datetime(),
    // Phase 5 D5-05: .optional() mandatory — keeps the .strict() schema
    // backward-compatible with producers that don't emit a directive.
    authDirective: AuthDirectiveSchema.optional(),
    // Handoff brief — set only on a `handoff_to_human` reply when a summary was
    // generated. Optional + additive (see HandoffMetaSchema).
    handoff: HandoffMetaSchema.optional(),
    // v2 lifecycle signal (see AgentsLifecycleSchema). Optional + additive →
    // v1 producers never emit it. v2 producers set it AND derive `status` from
    // it via `deriveReplyStatus` so the two never drift.
    lifecycle: AgentsLifecycleSchema.optional(),
    // Eventos de UI do turno (cards de UI generativa, opacos no `data` de cada
    // evento). NÃO É MAIS EMITIDO: o agents publica cada card uma vez só, em
    // `response.payload.ui` (o único campo que o conversation lê); repetir os
    // eventos aqui dobrava o tamanho da mensagem no SNS (#5311). Fica opcional
    // no schema só pela ordem de deploy: o conversation faz parse estrito, e um
    // reply em voo de um agents antigo, ainda com `uiEvents`, seria rejeitado se
    // o campo sumisse. Pode ser removido depois que o agents novo estiver em prod.
    uiEvents: z.array(SseEventSchema).optional(),
  })
  .strict();

export type AgentsReply = z.infer<typeof AgentsReplySchema>;
export type AgentsReplyTrace = z.infer<typeof AgentsReplyTraceSchema>;
export type AgentsReplyStatus = AgentsReply['status'];

/**
 * Single source for the reply `status`: derive it from the v2 `lifecycle`
 * signal plus whether the turn errored. `status` and `lifecycle` encode
 * overlapping meaning; deriving one from the other keeps them from drifting.
 * v2 producers call this; v1 producers keep setting `status` directly — both
 * remain valid on the wire.
 *
 *   error                       → 'error'  (an errored turn wins regardless)
 *   escalated                   → 'handoff_to_human'
 *   blocked | completed | none  → 'ok'     (`blocked` pauses the Kanban task
 *                                           for a human WITHOUT handing the
 *                                           conversation off)
 */
export function deriveReplyStatus(args: {
  lifecycle?: AgentsLifecycle;
  isError?: boolean;
}): AgentsReplyStatus {
  if (args.isError) return 'error';
  if (args.lifecycle === 'escalated') return 'handoff_to_human';
  return 'ok';
}
